import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { Plan } from './types';
import PlanCard from './components/PlanCard';
import PlanEditor from './components/PlanEditor';

const AdminPlanosPage = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [usageCounts, setUsageCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null | 'new'>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');

  const loadPlans = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('plans').select('*').order('display_order');
    const loaded = (data as Plan[]) || [];
    setPlans(loaded);

    const planIds = loaded.map((p) => p.id);
    if (planIds.length > 0) {
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('plan_id')
        .in('plan_id', planIds)
        .eq('status', 'active');

      const counts: Record<string, number> = {};
      (subs || []).forEach((s: any) => {
        counts[s.plan_id] = (counts[s.plan_id] || 0) + 1;
      });
      setUsageCounts(counts);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const handleDuplicate = async (plan: Plan) => {
    const { id, ...rest } = plan;
    const copy = {
      ...rest,
      name: `${plan.name} (cópia)`,
      slug: `${plan.slug}-copy-${Date.now()}`,
      is_active: false,
      is_public: false,
      is_featured: false,
      is_recommended: false,
      display_order: plans.length + 1,
    };
    await supabase.from('plans').insert(copy);
    await loadPlans();
  };

  const handleToggleActive = async (plan: Plan) => {
    await supabase.from('plans').update({ is_active: !plan.is_active }).eq('id', plan.id);
    setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_active: !plan.is_active } : p)));
  };

  const handleDelete = async (plan: Plan) => {
    if (!confirm(`Excluir o plano "${plan.name}"? Esta ação não pode ser desfeita.`)) return;
    await supabase.from('plans').delete().eq('id', plan.id);
    setPlans((prev) => prev.filter((p) => p.id !== plan.id));
  };

  const openEditor = (plan: Plan | null) => {
    setSelectedPlan(plan ?? 'new');
    setShowEditor(true);
  };

  const filtered = plans.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.slug.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || (filterStatus === 'active' ? p.is_active : !p.is_active);
    return matchSearch && matchStatus;
  });

  const totalActive = plans.filter((p) => p.is_active).length;
  const totalUsers = Object.values(usageCounts).reduce((acc, v) => acc + v, 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-700 text-white">Gestão de Planos</h1>
          <p className="text-white/40 text-[14px] mt-1">Configure recursos, preços, limites e comportamento comercial de cada plano</p>
        </div>
        <button
          onClick={() => openEditor(null)}
          className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />Novo Plano
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total de planos', value: plans.length, icon: 'ri-price-tag-3-line', color: 'text-violet-400' },
          { label: 'Planos ativos', value: totalActive, icon: 'ri-checkbox-circle-line', color: 'text-emerald-400' },
          { label: 'Planos em destaque', value: plans.filter((p) => p.is_featured).length, icon: 'ri-star-line', color: 'text-amber-400' },
          { label: 'Usuários em planos', value: totalUsers, icon: 'ri-user-line', color: 'text-cyan-400' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
              <i className={`${stat.icon} text-[18px] ${stat.color}`} />
            </div>
            <div>
              <p className="text-[20px] font-700 text-white">{stat.value}</p>
              <p className="text-[11px] text-white/40">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar plano..."
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-white text-sm outline-none focus:border-violet-500"
          />
        </div>
        <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1">
          {(['all', 'active', 'inactive'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${
                filterStatus === status ? 'bg-violet-600 text-white' : 'text-white/50 hover:text-white/80'
              }`}
            >
              {status === 'all' ? 'Todos' : status === 'active' ? 'Ativos' : 'Inativos'}
            </button>
          ))}
        </div>
        <p className="text-[12px] text-white/30 ml-auto">{filtered.length} plano(s)</p>
      </div>

      {/* Plan grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-white/30">
          <i className="ri-price-tag-3-line text-4xl mb-3" />
          <p className="text-[14px]">{plans.length === 0 ? 'Nenhum plano cadastrado' : 'Nenhum plano encontrado'}</p>
          {plans.length === 0 && (
            <button
              onClick={() => openEditor(null)}
              className="mt-4 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Criar primeiro plano
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filtered.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              usageCount={usageCounts[plan.id] || 0}
              isSelected={selectedPlan !== 'new' && selectedPlan?.id === plan.id}
              onEdit={() => openEditor(plan)}
              onDuplicate={() => handleDuplicate(plan)}
              onToggleActive={() => handleToggleActive(plan)}
              onDelete={() => handleDelete(plan)}
            />
          ))}
        </div>
      )}

      {/* Legend */}
      {plans.length > 0 && (
        <div className="mt-6 p-4 bg-white/3 border border-white/8 rounded-2xl">
          <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3">Campos disponíveis por plano</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {[
              'Preço mensal + anual com desconto',
              'Trial configurável',
              'Limites de contas, cartões, metas, lançamentos',
              'Relatórios, exportação CSV/PDF',
              'Insights com IA',
              'Multi-moedas',
              'Módulo de empréstimos',
              'Recorrência avançada',
              'Suporte prioritário',
              'Tolerância e regra de inadimplência',
              'Plano legado / interno / oculto',
            ].map((item) => (
              <span key={item} className="text-[11px] text-white/40 flex items-center gap-1">
                <i className="ri-check-line text-violet-400" />{item}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Editor panel */}
      {showEditor && (
        <PlanEditor
          plan={selectedPlan === 'new' ? null : selectedPlan}
          onClose={() => { setShowEditor(false); setSelectedPlan(null); }}
          onSaved={() => { setShowEditor(false); setSelectedPlan(null); loadPlans(); }}
        />
      )}
    </div>
  );
};

export default AdminPlanosPage;
